import { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
} from '@mui/x-data-grid'
import {
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  EditOutlined as EditOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { Link, useNavigate } from 'react-router-dom'
import { isNil } from 'lodash'
import { type Resource } from 'types/api'
import { dataTablePromotionDateFormatter } from 'utils/util'
import { DataTable } from 'components/Shared/DataTable'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { useGetResources } from 'hooks/api/Resources/useGetResources'
import { useSnackbar } from 'stores/useSnackbar'
import { DeleteResourceFileModal } from './DeleteResourceFile/DeleteResourceFileModal'
import { DeleteResourceFileRenderCell } from './DeleteResourceFile/DeleteResourceFileRenderCell'
import { useGetResourceCategories } from 'hooks/api/Resources/useGetResourceCategories'
import { useCreateResourceCategory } from 'hooks/api/Resources/useCreateResourceCategory'
import { usePutResourceCategory } from 'hooks/api/Resources/usePutResourceCategory'
import { useDeleteResourceCategoryById } from 'hooks/api/Resources/useDeleteResourceCategoryById'
import { ConfirmModal } from 'components/ConfirmModal/ConfirmModal'
import {
  type ResourceReorderPayload,
  useSaveResourceOrder,
} from 'hooks/api/Resources/useSaveResourceOrder'

type ResourceSection = number | 'None'

type SectionedResources = Record<ResourceSection, Resource[]>

const UNCATEGORIZED_SECTION = 'None' as const

const haveSameOrder = (left: Resource[], right: Resource[]): boolean => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((resource, index) => resource.id === right[index]?.id)
}

const reorderById = (
  rows: Resource[],
  draggedId: number,
  targetId: number
): Resource[] => {
  const draggedIndex = rows.findIndex((row) => row.id === draggedId)
  const targetIndex = rows.findIndex((row) => row.id === targetId)

  if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) {
    return rows
  }

  const updatedRows = [...rows]
  const [draggedRow] = updatedRows.splice(draggedIndex, 1)
  updatedRows.splice(targetIndex, 0, draggedRow)

  return updatedRows
}

const buildOrderLookup = (rows: Resource[]): Map<number, number> => {
  return new Map(rows.map((resource, index) => [resource.id, index]))
}

const sortRowsByLookup = (
  rows: Resource[],
  orderById: Map<number, number>
): Resource[] => {
  return [...rows].sort((left, right) => {
    const leftOrder = orderById.get(left.id) ?? Number.MAX_SAFE_INTEGER
    const rightOrder = orderById.get(right.id) ?? Number.MAX_SAFE_INTEGER
    return leftOrder - rightOrder
  })
}

export const JJResourcesList = () => {
  const [resourceSections, setResourceSections] = useState<ResourceSection[]>([
    UNCATEGORIZED_SECTION,
  ])
  const [filteredDataBySection, setFilteredDataBySection] = useState<
    Partial<Record<ResourceSection, Resource[]>>
  >({})
  const [orderedResourcesBySection, setOrderedResourcesBySection] =
    useState<SectionedResources>({ [UNCATEGORIZED_SECTION]: [] })
  const [savedResourcesBySection, setSavedResourcesBySection] =
    useState<SectionedResources>({ [UNCATEGORIZED_SECTION]: [] })
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [categoryFormError, setCategoryFormError] = useState<string | null>(
    null
  )
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false)
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null)
  const [editCategoryName, setEditCategoryName] = useState('')
  const [editCategoryFormError, setEditCategoryFormError] = useState<
    string | null
  >(null)
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<{
    id: number
    name: string
  } | null>(null)
  const [draggedResource, setDraggedResource] = useState<{
    section: ResourceSection
    resourceId: number
  } | null>(null)
  const [resourceRowData, setResourceRowData] = useState<Resource | null>()
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const navigate = useNavigate()
  const useGetResourcesQuery = useGetResources()
  const useGetResourceCategoriesQuery = useGetResourceCategories()
  const resources = useGetResourcesQuery.data ?? []
  const categories = useGetResourceCategoriesQuery.data ?? []

  const uncategorizedCategoryIds = useMemo(() => {
    return new Set(
      categories
        .filter((category) => category.name.trim().toLowerCase() === 'none')
        .map((category) => category.id)
    )
  }, [categories])

  const getSectionLabel = (section: ResourceSection): string => {
    if (section === UNCATEGORIZED_SECTION) {
      return 'Uncategorized'
    }

    return (
      categories.find((category) => category.id === section)?.name ??
      `Category ${String(section)}`
    )
  }

  const createCategoryMutation = useCreateResourceCategory({
    onSuccess: () => {
      setIsCreateCategoryOpen(false)
      setNewCategoryName('')
      setCategoryFormError(null)
      setSnackbarMessage('Resource category created successfully', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
    onError: () => {
      setSnackbarMessage('Error creating resource category', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const putCategoryMutation = usePutResourceCategory({
    onSuccess: () => {
      setIsEditCategoryOpen(false)
      setEditCategoryId(null)
      setEditCategoryName('')
      setEditCategoryFormError(null)
      setSnackbarMessage('Resource category updated successfully', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
    onError: () => {
      setSnackbarMessage('Error updating resource category', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const deleteCategoryMutation = useDeleteResourceCategoryById({
    onSuccess: () => {
      setDeleteCategoryTarget(null)
      setSnackbarMessage('Resource category deleted successfully', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
    onError: () => {
      setSnackbarMessage('Error deleting resource category', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  useEffect(() => {
    const sortedCategoryIds = [...categories]
      .filter((category) => category.name.trim().toLowerCase() !== 'none')
      .sort(
        (left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0)
      )
      .map((category) => category.id)

    setResourceSections([...sortedCategoryIds, UNCATEGORIZED_SECTION])
  }, [categories])

  const saveResourceOrder = useSaveResourceOrder({
    onSuccess: (payload) => {
      const section = payload.resourceCategoryId
      setSavedResourcesBySection((current) => ({
        ...current,
        [section]: [...(orderedResourcesBySection[section] ?? [])],
      }))

      setSnackbarMessage(
        `${getSectionLabel(section)} order saved successfully.`,
        'success',
        {
          vertical: 'top',
          horizontal: 'right',
        }
      )
    },
    onError: () => {
      setSnackbarMessage('Error saving resource order', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const handleFilteredData = (section: ResourceSection) => {
    return (filteredData: Resource[]) => {
      setFilteredDataBySection((current) => ({
        ...current,
        [section]: filteredData,
      }))
    }
  }

  const groupedResources = useMemo(() => {
    const grouped = Object.fromEntries(
      resourceSections.map((section) => [section, [] as Resource[]])
    ) as SectionedResources

    for (const resource of resources) {
      const section: ResourceSection =
        resource.resourceCategoryId == null ||
        uncategorizedCategoryIds.has(resource.resourceCategoryId)
          ? UNCATEGORIZED_SECTION
          : resource.resourceCategoryId

      grouped[section] ??= []

      grouped[section].push(resource)
    }

    return grouped
  }, [resourceSections, resources, uncategorizedCategoryIds])

  useEffect(() => {
    setOrderedResourcesBySection(groupedResources)
    setSavedResourcesBySection(groupedResources)
    setFilteredDataBySection({})
  }, [groupedResources])

  const buildReorderPayload = (
    section: ResourceSection
  ): ResourceReorderPayload => {
    if (section === UNCATEGORIZED_SECTION) {
      throw new Error('Cannot save order for uncategorized resources.')
    }

    return {
      resourceCategoryId: section,
      resourceIds: (orderedResourcesBySection[section] ?? []).map(
        (resource) => resource.id
      ),
    }
  }

  const hasSectionOrderChanges = (section: ResourceSection): boolean => {
    return !haveSameOrder(
      orderedResourcesBySection[section] ?? [],
      savedResourcesBySection[section] ?? []
    )
  }

  const handleSaveOrder = (section: ResourceSection) => {
    if (section === UNCATEGORIZED_SECTION) {
      return
    }

    const payload = buildReorderPayload(section)

    saveResourceOrder.mutate(payload)
  }

  const handleDragStart = (section: ResourceSection, resourceId: number) => {
    setDraggedResource({ section, resourceId })
  }

  const handleDrop = (section: ResourceSection, targetResourceId: number) => {
    if (draggedResource?.section !== section) {
      return
    }

    const draggedResourceId = draggedResource.resourceId
    const reorderedRows = reorderById(
      orderedResourcesBySection[section] ?? [],
      draggedResourceId,
      targetResourceId
    )

    if (
      !haveSameOrder(orderedResourcesBySection[section] ?? [], reorderedRows)
    ) {
      setOrderedResourcesBySection({
        ...orderedResourcesBySection,
        [section]: reorderedRows,
      })

      const currentFilteredRows = filteredDataBySection[section]

      if (currentFilteredRows != null) {
        const orderById = buildOrderLookup(reorderedRows)

        setFilteredDataBySection({
          ...filteredDataBySection,
          [section]: sortRowsByLookup(currentFilteredRows, orderById),
        })
      }
    }

    setDraggedResource(null)
  }

  const getColumns = (
    section: ResourceSection
  ): Array<GridColDef<Resource>> => {
    return [
      {
        field: '__reorder__',
        headerName: '',
        width: 56,
        cellClassName: 'reorder-drop-cell',
        headerClassName: 'reorder-drop-header',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<Resource>) => {
          return (
            <Stack
              width="100%"
              height="100%"
              alignItems="center"
              justifyContent="center"
              onDragOver={(event) => {
                event.preventDefault()
              }}
              onDrop={(event) => {
                event.preventDefault()
                handleDrop(section, params.row.id)
              }}
            >
              <IconButton
                size="small"
                draggable
                onDragStart={() => {
                  handleDragStart(section, params.row.id)
                }}
                sx={{ cursor: 'grab' }}
                aria-label={`Reorder ${params.row.title}`}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Stack>
          )
        },
      },
      {
        field: 'title',
        minWidth: 200,
        headerName: 'Title',
        flex: 2,
      },
      {
        field: 'resourceCategoryId',
        minWidth: 100,
        headerName: 'Category',
        flex: 2,
        valueGetter: (params) => {
          const row = params.row

          if (
            row.resourceCategoryId == null ||
            uncategorizedCategoryIds.has(row.resourceCategoryId)
          ) {
            return 'Uncategorized'
          }

          return (
            categories.find(
              (category) => category.id === row.resourceCategoryId
            )?.name ?? `Category ${String(row.resourceCategoryId)}`
          )
        },
      },
      {
        field: 'description',
        minWidth: 100,
        headerName: 'Description',
        flex: 2,
      },
      {
        field: 'createdOn',
        minWidth: 50,
        headerName: 'Date',
        flex: 1,
        valueFormatter: dataTablePromotionDateFormatter(false, 'Start'),
      },
      {
        field: '__menu__',
        headerName: '',
        align: 'left',
        renderCell: (params: GridRenderCellParams<Resource>) => {
          return (
            <DeleteResourceFileRenderCell
              params={params}
              setResourceRowData={setResourceRowData}
            />
          )
        },
      },
    ]
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (
      params.field !== '__menu__' &&
      params.field !== '__reorder__' &&
      params.id !== null
    ) {
      navigate(`/JJUpdates/Resources/${params.id}`)
    }
  }

  const submitNewCategory = () => {
    const trimmedName = newCategoryName.trim()

    if (trimmedName.length === 0) {
      setCategoryFormError('Must enter a category name')
      return
    }

    setCategoryFormError(null)
    const nextDisplayOrder =
      categories.length === 0
        ? 0
        : Math.max(
            ...categories.map((category) => category.displayOrder ?? 0)
          ) + 1

    createCategoryMutation.mutate({
      name: trimmedName,
      displayOrder: nextDisplayOrder,
    })
  }

  const openEditCategoryModal = (section: number) => {
    const category = categories.find((item) => item.id === section)

    if (category == null) {
      setSnackbarMessage('Unable to find category to edit', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
      return
    }

    setEditCategoryId(category.id)
    setEditCategoryName(category.name)
    setEditCategoryFormError(null)
    setIsEditCategoryOpen(true)
  }

  const submitEditCategory = () => {
    const trimmedName = editCategoryName.trim()

    if (trimmedName.length === 0) {
      setEditCategoryFormError('Must enter a category name')
      return
    }

    if (editCategoryId == null) {
      setEditCategoryFormError('Category not found')
      return
    }

    const category = categories.find((item) => item.id === editCategoryId)

    setEditCategoryFormError(null)
    putCategoryMutation.mutate({
      id: editCategoryId,
      name: trimmedName,
      displayOrder: category?.displayOrder,
    })
  }

  return (
    <>
      <Stack
        direction={{ md: 'row', sm: 'column', xs: 'column' }}
        gap={{ md: 3, sm: 2, xs: 2 }}
        mb={{ md: 4, sm: 2, xs: 2 }}
        justifyContent={'flex-end'}
      >
        <Button
          variant="outlined"
          onClick={() => {
            setIsCreateCategoryOpen(true)
          }}
        >
          Create New Category
        </Button>
        <Link to="/JJUpdates/Resources/New">
          <Button variant="contained">Create New Resource</Button>
        </Link>
      </Stack>
      {resourceSections.map((section) => {
        const sectionResources = orderedResourcesBySection[section] ?? []
        const sectionRows = filteredDataBySection[section] ?? sectionResources
        const sectionLabel = getSectionLabel(section)
        const hasPendingOrderChanges = hasSectionOrderChanges(section)
        const isUncategorizedSection = section === UNCATEGORIZED_SECTION
        const categoryCanBeDeleted =
          !isUncategorizedSection && sectionResources.length === 0

        return (
          <Accordion key={section} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                pr={1}
              >
                <Typography variant="h4">{sectionLabel}</Typography>
                {!isUncategorizedSection && (
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      aria-label={`Edit ${sectionLabel} category`}
                      onClick={(event) => {
                        event.stopPropagation()
                        openEditCategoryModal(section)
                      }}
                      onFocus={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Delete ${sectionLabel} category`}
                      onClick={(event) => {
                        event.stopPropagation()
                        setDeleteCategoryTarget({
                          id: section,
                          name: sectionLabel,
                        })
                      }}
                      onFocus={(event) => {
                        event.stopPropagation()
                      }}
                      disabled={
                        !categoryCanBeDeleted ||
                        deleteCategoryMutation.isPending
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                justifyContent={'space-between'}
                gap={2}
              >
                <Stack width={{ xs: '100%', md: '50%' }}>
                  <GridSearchField<Resource>
                    placeholder={`Search ${sectionLabel} Resources`}
                    data={sectionResources}
                    handleFilteredData={handleFilteredData(section)}
                    fullWidth
                  />
                </Stack>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleSaveOrder(section)
                  }}
                  disabled={
                    isUncategorizedSection ||
                    !hasPendingOrderChanges ||
                    saveResourceOrder.isPending
                  }
                >
                  Save Order
                </Button>
              </Stack>
              <DataTable
                data-testid={`data-table-${section}`}
                sortFieldName={undefined}
                columns={getColumns(section)}
                rows={sectionRows}
                onCellClick={selectCell}
              />
            </AccordionDetails>
          </Accordion>
        )
      })}
      <DeleteResourceFileModal
        data={resourceRowData}
        isModalOpen={!isNil(resourceRowData)}
        toggleIsOpen={() => setResourceRowData(undefined)}
      />
      <Modal
        open={isEditCategoryOpen}
        onClose={() => {
          if (!putCategoryMutation.isPending) {
            setIsEditCategoryOpen(false)
            setEditCategoryFormError(null)
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: { xs: '90%', md: 420 },
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h4">Edit Category Name</Typography>
            {editCategoryFormError != null && (
              <Alert severity="error">{editCategoryFormError}</Alert>
            )}
            <TextField
              placeholder="Category Name"
              inputProps={{ 'aria-label': 'Category Name' }}
              value={editCategoryName}
              onChange={(event) => {
                setEditCategoryName(event.target.value)
                if (editCategoryFormError != null) {
                  setEditCategoryFormError(null)
                }
              }}
              fullWidth
              autoFocus
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="text"
                onClick={() => {
                  setIsEditCategoryOpen(false)
                  setEditCategoryFormError(null)
                }}
                disabled={putCategoryMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={submitEditCategory}
                disabled={putCategoryMutation.isPending}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      <Modal
        open={isCreateCategoryOpen}
        onClose={() => {
          if (!createCategoryMutation.isPending) {
            setIsCreateCategoryOpen(false)
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: { xs: '90%', md: 420 },
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h4">Create New Category</Typography>
            {categoryFormError != null && (
              <Alert severity="error">{categoryFormError}</Alert>
            )}
            <TextField
              placeholder="Category Name"
              inputProps={{ 'aria-label': 'Category Name' }}
              value={newCategoryName}
              onChange={(event) => {
                setNewCategoryName(event.target.value)
                if (categoryFormError != null) {
                  setCategoryFormError(null)
                }
              }}
              fullWidth
              autoFocus
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="text"
                onClick={() => {
                  setIsCreateCategoryOpen(false)
                  setCategoryFormError(null)
                }}
                disabled={createCategoryMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={submitNewCategory}
                disabled={createCategoryMutation.isPending}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      <ConfirmModal
        isModalOpen={deleteCategoryTarget != null}
        toggleIsOpen={() => {
          if (!deleteCategoryMutation.isPending) {
            setDeleteCategoryTarget(null)
          }
        }}
        onCancel={() => {
          if (!deleteCategoryMutation.isPending) {
            setDeleteCategoryTarget(null)
          }
        }}
        onConfirm={() => {
          if (deleteCategoryTarget == null) {
            return
          }

          deleteCategoryMutation.mutate(deleteCategoryTarget.id)
        }}
        confirmMessage="Are you sure you want to delete this category?"
        secondaryMessage={`${deleteCategoryTarget?.name ?? 'category'} will be deleted`}
      />
    </>
  )
}
